/* =========================================================================
   LAZ Skincare — my routines

   The member's collection: list, view, cart, delete. This page manages
   routines; it does not edit them. "View" loads a routine into the working
   copy and hands off to /pages/routine, so there is exactly one editing
   surface.

   Viewing is the moment work can be lost, because it replaces the working
   copy wholesale. It therefore flushes any pending write-through first, and
   only asks the shopper to choose if that flush fails.

   LSC - Two buttons per row now, down from four.

   Rename went first: it was the only one of the four that did not need the
   routine open, and it duplicated the Rename control that already sits beside
   the title on /pages/routine. One place to rename, and this page stays a
   shelf you pick from rather than a second editor.

   View went next, for the same reason in a different form: the whole left
   side of the row already opens the routine, so the button was a second way
   to do the one thing the row does by default. What is left is the pair that
   acts on a routine without opening it — Add to cart, and Delete.
   ========================================================================= */

(function () {
  "use strict";

  const R = window.LazRoutine;
  if (!R) return;

  const root = document.querySelector("[data-lsc-my-routines]");
  if (!root) return;

  const listEl = root.querySelector("[data-my-routines-list]");
  if (!listEl) return; // guest view, nothing to wire

  const routineUrl = root.dataset.routineUrl || "/pages/routine";

  /* ---------------------------------------------------------------------
     load and render
     --------------------------------------------------------------------- */

  async function load() {
    listEl.setAttribute("aria-busy", "true");
    try {
      const res = await R.savedRoutines();
      render(res.routines || []);
    } catch (err) {
      listEl.textContent = "";
      listEl.appendChild(
        R.el(
          "p",
          "text-sm text-opacity",
          err.status === 401
            ? "Your session expired. Log in again to see your routines."
            : `We couldn't load your routines. ${err.message}`
        )
      );
    } finally {
      listEl.setAttribute("aria-busy", "false");
    }
  }

  function render(routines) {
    listEl.textContent = "";

    if (!routines.length) {
      const empty = R.el("div", "lsc-routine-empty");
      empty.appendChild(R.el("h2", "heading text-xl", "No saved routines yet"));
      empty.appendChild(
        R.el(
          "p",
          "text-sm text-opacity",
          "Build a routine, then save it to your account to keep more than one."
        )
      );
      const link = R.el("a", "button button--primary");
      link.href = routineUrl;
      link.setAttribute("is", "hover-button");
      link.innerHTML =
        '<span class="btn-fill" data-fill></span><span class="btn-text">Build a routine</span>';
      empty.appendChild(link);
      listEl.appendChild(empty);
      return;
    }

    /* Most recently touched first — the one you are most likely here for. */
    const sorted = [...routines].sort((a, b) =>
      String(b.updated_at || "").localeCompare(String(a.updated_at || ""))
    );

    const state = R.read();
    const ul = R.el("ul", "lsc-routine-saved__items");
    ul.setAttribute("role", "list");

    for (const routine of sorted) {
      ul.appendChild(buildRow(routine, routine.id === state.source_id, state.unsynced));
    }

    listEl.appendChild(ul);
  }

  /* ---------------------------------------------------------------------
     icons
     --------------------------------------------------------------------- */

  /* LSC - The eye glyph went with the View button it labelled. Opening a
     routine is the row itself now, and an unused constant is a thing the next
     reader has to rule out. */
  const CART_GLYPH =
    '<circle cx="9.5" cy="20" r="1.4"/><circle cx="17.5" cy="20" r="1.4"/>' +
    '<path d="M2.5 3.5h2.8l2.5 11.1a1.6 1.6 0 0 0 1.6 1.3h8.3a1.6 1.6 0 0 0 1.6-1.3L21 7.4H6.2"/>';

  const TRASH_GLYPH =
    '<path d="M3.5 6.2h17"/>' +
    '<path d="M8.6 6.2V4.6a1.4 1.4 0 0 1 1.4-1.4h4a1.4 1.4 0 0 1 1.4 1.4v1.6"/>' +
    '<path d="M18.4 6.2l-.8 13.1a1.8 1.8 0 0 1-1.8 1.7H8.2a1.8 1.8 0 0 1-1.8-1.7L5.6 6.2"/>' +
    '<path d="M10.3 10.6v6M13.7 10.6v6"/>';

  /* LSC - .lsc-routine__toolbar-icon rather than a class of its own: it is the
     glyph spacing the routine toolbar, the share panel's copy button and the
     saved-routines link all already use, and an icon inside a button is the
     same problem in all four places. */
  function iconSpan(paths) {
    const span = R.el("span", "lsc-routine__toolbar-icon");
    span.setAttribute("aria-hidden", "true");
    span.innerHTML =
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" ' +
      'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
      paths +
      "</svg>";
    return span;
  }

  /* ---------------------------------------------------------------------
     a row
     --------------------------------------------------------------------- */

  function buildRow(routine, isActive, unsynced) {
    const li = R.el("li", "lsc-routine-saved__item");
    if (isActive) li.classList.add("is-editing");

    /* LSC - The whole left side of the row opens the routine, not just a
       link on the name: at this size the name, the count and the note all
       read as one thing, and making only part of them live means aiming.

       A real <button> rather than a div with a click handler, so it is in
       the tab order and answers Enter and Space without any of that being
       reimplemented. That is also why the three lines inside are spans: a
       <button> may only contain phrasing content, and a <p> in here would be
       invalid markup that browsers then re-parse out of the button. */
    const open = R.el("button", "lsc-routine-saved__open");
    open.type = "button";
    open.setAttribute("aria-label", `View ${routine.name}`);

    open.appendChild(R.el("span", "lsc-routine-saved__name font-medium", routine.name));

    const n = (routine.items || []).length;
    const parts = [`${n} ${n === 1 ? "product" : "products"}`];
    if (isActive) parts.push(unsynced ? "open, with unsaved changes" : "open for editing");
    const updated = formatDate(routine.updated_at);
    if (updated) parts.push(`updated ${updated}`);
    open.appendChild(R.el("span", "text-xs text-opacity", parts.join(" · ")));

    if (routine.note) {
      open.appendChild(
        R.el("span", "lsc-routine-saved__note text-sm text-opacity", routine.note)
      );
    }

    open.addEventListener("click", () => openRoutine(routine));
    li.appendChild(open);

    const actions = R.el("div", "lsc-routine-saved__actions");

    /* LSC - No View button: the whole left side of the row is the button that
       opens the routine, and a second control doing the same thing from the
       far right of the same row was one too many. The clickable region is a
       real <button> carrying `View <name>` as its accessible name, so the
       action still announces itself — it is just not drawn twice. */
    const cart = actionButton("cart", "Add to cart", CART_GLYPH);
    cart.addEventListener("click", () => addToCart(cart, routine));

    const del = actionButton("delete", "Delete", TRASH_GLYPH);
    del.addEventListener("click", () => confirmDelete(del, routine));

    actions.append(cart, del);
    li.appendChild(actions);
    return li;
  }

  /* LSC - The theme's own button, not a bespoke one: .button--secondary, the
     absolutely-positioned .btn-fill that slides up on hover, and .btn-text
     riding above it. The stylesheet then only has to say what colour two of
     the three are, exactly as the Add to cart buttons elsewhere do.

     document.createElement with the `is` option rather than setAttribute
     afterwards: `is` is only honoured at creation time for a customized
     built-in, so setting it later gives you the class but not the upgraded
     element. Guarded, because a browser without customized built-ins throws
     nothing but also honours nothing — and the fill is CSS either way.

     Icon and label both, always, in the markup. The label is only hidden
     visually below 750px — the aria-label carries it at every width, so the
     icon-only version on a phone is not a nameless button. */
  function actionButton(variant, label, glyph) {
    let button;
    try {
      button = document.createElement("button", { is: "hover-button" });
    } catch {
      button = document.createElement("button");
    }
    button.type = "button";
    /* LSC - The colour marker: one class per standard, rather than the
       stylesheet naming each member by its own attribute. See the standards
       at the top of lsc-routine.css. */
    const ground = variant === "cart" ? "lsc-btn--buy" : variant === "delete" ? "lsc-btn--add" : "";
    button.className =
      `button button--secondary ${ground} lsc-routine-saved__action lsc-routine-saved__action--${variant}`.replace(/\s+/g, " ");
    button.setAttribute("is", "hover-button");
    button.setAttribute("aria-label", label);

    const fill = R.el("span", "btn-fill");
    fill.setAttribute("data-fill", "");

    const text = R.el("span", "btn-text");
    text.append(iconSpan(glyph), R.el("span", "lsc-routine-saved__action-label", label));

    button.append(fill, text);
    return button;
  }

  const labelOf = (button) => button.querySelector(".lsc-routine-saved__action-label");

  function formatDate(iso) {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    try {
      return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
      return "";
    }
  }

  /* ---------------------------------------------------------------------
     actions
     --------------------------------------------------------------------- */

  /* Viewing replaces the working copy, so anything not yet on the account is
     about to disappear. Try to land it first; only involve the shopper if
     that fails. The old version of this guard only warned when there was no
     bound routine at all, which meant the one case that actually lost work —
     a bound routine with pending edits — passed silently. */
  async function openRoutine(routine) {
    const state = R.read();

    if (state.source_id && state.unsynced) {
      const flushed = await R.syncNow();
      if (!flushed) {
        const ok = window.confirm(
          `Changes to “${state.name || "your open routine"}” haven't reached your account yet. ` +
            `Open “${routine.name}” anyway and discard them?`
        );
        if (!ok) return;
      }
    } else if (state.items.length && !state.source_id) {
      const ok = window.confirm(
        `Open “${routine.name}”? The routine you're building in this browser hasn't been saved and will be replaced.`
      );
      if (!ok) return;
    }

    /* LSC - Awaited, and it has to be. R.replace() now flushes any unsynced
       edit to the account before it overwrites the working copy, so it is
       async — and navigating before it resolves would load the routine page
       with the PREVIOUS routine still in localStorage. */
    await R.replace(routine.items || [], {
      source_id: routine.id,
      name: routine.name,
      note: routine.note,
    });
    window.location.href = routineUrl;
  }

  async function addToCart(button, routine) {
    const items = routine.items || [];
    if (!items.length) return;

    /* Only the label text is swapped, so the cart glyph stays put and the
       button does not change width mid-request. On a phone the label is
       hidden anyway, which is what the is-busy class is for. */
    const label = labelOf(button);
    const original = label.textContent;
    button.disabled = true;
    button.classList.add("is-busy");
    label.textContent = "Working…";
    button.setAttribute("aria-label", "Working…");

    try {
      await R.addToCart(items);
      R.toast(
        `Added ${items.length} ${items.length === 1 ? "product" : "products"} to your cart.`,
        "success"
      );
    } catch (err) {
      R.toast(
        `${err.message} Nothing was added — open the routine and add the products individually.`,
        "error"
      );
    } finally {
      button.disabled = false;
      button.classList.remove("is-busy");
      label.textContent = original;
      button.setAttribute("aria-label", original);
    }
  }

  /* LSC - The button is disabled for the length of the request.

     window.confirm serialises the first double-click, but the trash stayed
     live while the DELETE was in flight: a second press re-asked and fired a
     second DELETE for an id that was already gone, so the shopper got a
     success toast and then an error about a routine that no longer existed.
     Add to cart beside it has always done this; delete simply did not. */
  async function confirmDelete(button, routine) {
    if (button.disabled) return;

    const ok = window.confirm(`Delete “${routine.name}”? This can't be undone.`);
    if (!ok) return;

    button.disabled = true;

    try {
      await R.deleteRoutine(routine.id);
      /* The working copy survives the delete — it just stops being tied to a
         saved routine, so the next save asks for a name again. */
      if (R.read().source_id === routine.id) {
        const state = R.read();
        await R.replace(state.items, { source_id: null, name: state.name, note: state.note });
      }
      R.toast("Deleted.", "success");
      /* load() rebuilds the list, so this button goes with it — no need to
         re-enable on the success path. */
      load();
    } catch (err) {
      button.disabled = false;
      R.toast(err.message, "error");
    }
  }

  load();
})();
